import styled from "styled-components";
import { Form, Input, Button, Upload } from "antd";
import { UseQueryResult, useMutation } from "@tanstack/react-query";
import {
  openNotification,
  IFileRequest,
  createFile,
  updateFile,
  FileType,
  FORMAT,
  uploadFile,
  isEmpty,
  BASE_URL,
  IErrorResponse,
} from "@dataone/lib";
import SubHeader from "./SubHeader";
import { ErrorStep } from "../../steps/ErrorStep";
import { IDataSourceApp } from "../datasets-sources/DatasetsSources.config";
import { useState } from "react";
import { RcFile } from "antd/es/upload";
import type { UploadProps } from "antd";
import { AxiosError } from "axios";
import { UploadIcon } from "@dataone/icons";

interface UpdateFileFormProps {
  failed: boolean;
  fileData: IFileRequest | null;
  dataSource: IDataSourceApp;
  formMode: "update" | "create";
  setILoading: (submitClicked: boolean) => void;
  setCurrent: (current: number) => void;
  setFailed: (failed: boolean) => void;
  onCreateSuccess: () => void;
  refetchDatasets?: (options?: {
    throwOnError: boolean;
    cancelRefetch: boolean;
  }) => Promise<UseQueryResult>;
}

const FileFormat = {
  [FileType.EXCEL]: {
    types: FORMAT.excel.types,
    extensions: FORMAT?.excel?.extensions,
  },
  [FileType.CSV]: {
    types: FORMAT.csv.types,
    extensions: FORMAT.csv.extensions,
  },
  [FileType.WORD]: {
    types: FORMAT.word.types,
    extensions: FORMAT.word.extensions,
  },
  [FileType.PDF]: {
    types: FORMAT.pdf.types,
    extensions: FORMAT.pdf.extensions,
  },
  [FileType.TXT]: {
    types: FORMAT.txt.types,
    extensions: FORMAT.txt.extensions,
  },
  [FileType.PPTX]: {
    types: FORMAT.pptx.types,
    extensions: FORMAT.pptx.extensions,
  },
};

interface IUploadedDriveCredentials {
  type: string;
  project_id: string;
  private_key_id: string;
  private_key: string;
  client_email: string;
  client_id: string;
  auth_uri: string;
  token_uri: string;
  auth_provider_x509_cert_url: string;
  client_x509_cert_url: string;
  universe_domain: string;
}

function instanceOfUploadedDriveCredentials(
  object: any
): object is IUploadedDriveCredentials {
  return (
    "type" in object &&
    "project_id" in object &&
    "private_key_id" in object &&
    "private_key" in object &&
    "client_email" in object &&
    "client_id" in object &&
    "auth_uri" in object &&
    "token_uri" in object &&
    "auth_provider_x509_cert_url" in object &&
    "client_x509_cert_url" in object &&
    "universe_domain" in object
  );
}

type FileFormatType = keyof typeof FileFormat;

const UpdateFileForm = ({
  formMode,
  failed,
  fileData,
  dataSource,
  setILoading,
  setCurrent,
  setFailed,
  onCreateSuccess,
  refetchDatasets,
}: UpdateFileFormProps) => {
  const [fileForm] = Form.useForm();

  const pathValue = Form.useWatch("path", fileForm);
  const fileListValue = Form.useWatch("file", fileForm)?.fileList;

  const { mutate: addFile, isLoading: isCreating } = useMutation(createFile);
  const { mutate: editFile, isLoading: isUpdating } = useMutation(updateFile);
  const { mutate: upload } = useMutation(uploadFile);

  const [uploadError, setUploadError] = useState("");
  const [driveCredentials, setDriveCredentials] = useState("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const beforeFileUpload = async (file: RcFile) => {
    const isRightFormat = FileFormat[
      dataSource.type as FileFormatType
    ].types.includes(file.type);
    const isLarge = file.size > FORMAT.MAX_DOC_SIZE;

    if (!isRightFormat) {
      setUploadError("Please upload the right format file!");
      return false;
    }
    if (isLarge) {
      setUploadError("File must be less than 16 MB.");
      return false;
    }
    setUploadError("");
    return true;
  };

  const handleOnSuccess = (message: string) => {
    // openNotification(message);
    setCurrent(2);
  };

  const isUploadedFile = (path?: string) => {
    return path?.startsWith(`${BASE_URL}/storage/public/`);
  };

  const uploadProps: UploadProps = {
    defaultFileList: isUploadedFile(fileData?.path)
      ? [
          {
            uid: "1",
            name: fileData?.path
              .split("/")
              .at(-1)
              ?.substring(fileData?.path.split("/").at(-1)!.indexOf("_") + 1)!,
            status: "done",
            url: fileData?.path,
          },
        ]
      : [],
  };

  const handleSubmitForm = async (values: IFileRequest) => {
    setILoading(true);

    const credentials = driveCredentials
      ? (JSON.parse(driveCredentials) as IUploadedDriveCredentials)
      : undefined;

    const credentialsValue =
      dataSource.type === FileType.DRIVE && credentials
        ? {
            projectId: credentials.project_id,
            privateKeyId: credentials.private_key_id,
            privateKey: credentials.private_key,
            clientEmail: credentials.client_email,
            clientId: credentials.client_id,
            clientX509CertUrl: credentials.client_x509_cert_url,
          }
        : undefined;

    formMode === "update"
      ? fileData &&
        editFile(
          {
            ...values,
            type: dataSource.type as FileType,
            path: fileListValue?.[0].response || values.path,
            id: fileData.id,
            driveCredentials: credentialsValue,
          },
          {
            onSuccess: ({ message }) => {
              handleOnSuccess(message);
              setILoading(false);
              refetchDatasets?.();
            },
            onError: (error) => {
              setILoading(false);
              setFailed(true);
              const { response } = error as AxiosError<IErrorResponse>;
              setErrorMessage(response?.data.message!);
            },
          }
        )
      : addFile(
          {
            ...values,
            type: dataSource.type as FileType,
            path: fileListValue?.[0].response || values.path,
            driveCredentials: credentialsValue,
          },
          {
            onSuccess: ({ message }) => {
              handleOnSuccess(message);
              setILoading(false);
              onCreateSuccess();
            },
            onError: (error) => {
              setILoading(false);
              setFailed(true);
              const { response } = error as AxiosError<IErrorResponse>;
              setErrorMessage(response?.data.message!);
            },
          }
        );
  };

  const rules = [
    {
      whitespace: true,
      required: true,
      message: "Please provide a value for this field.",
    },
  ];

  return (
    <>
      {failed ? (
        <ErrorStep errorMessage={errorMessage} />
      ) : (
        <>
          <StyledFileForm $maxHeight="431px">
            <SubHeader icon={dataSource?.icon} label={dataSource.title} />
            <Form
              form={fileForm}
              className="file-form"
              layout="vertical"
              onFinish={handleSubmitForm}
              {...(fileData && { initialValues: fileData })}
            >
              <Form.Item
                rules={rules}
                name="displayName"
                label={<StyledLabel>Display name</StyledLabel>}
              >
                <Input
                  placeholder="Display name"
                  maxLength={255}
                  className="input"
                />
              </Form.Item>
              {[
                FileType.DRIVE,
                FileType.EXCEL,
                FileType.PDF,
                FileType.PPTX,
                FileType.WORD,
              ].includes(dataSource.type as FileType) && (
                <Form.Item
                  style={{
                    display:
                      fileListValue?.length || isUploadedFile(pathValue)
                        ? "none"
                        : "block",
                  }}
                  name="path"
                  rules={
                    fileListValue?.length || isUploadedFile(pathValue)
                      ? []
                      : rules
                  }
                  label={<StyledLabel>Link</StyledLabel>}
                >
                  <Input
                    maxLength={255}
                    className="input"
                    placeholder={
                      dataSource.type === FileType.EXCEL
                        ? "Google Sheets URL"
                        : dataSource.type === FileType.WORD
                        ? "Google Docs URL"
                        : dataSource.type === FileType.PPTX
                        ? "Google Slides URL"
                        : dataSource.type === FileType.DRIVE
                        ? "Google Drive URL"
                        : "URL"
                    }
                  />
                </Form.Item>
              )}
              {dataSource.type === FileType.DRIVE ? (
                <Form.Item
                  name="driveCredentials"
                  rules={
                    formMode === "create"
                      ? [
                          {
                            validator: async () => {
                              if (!driveCredentials)
                                return Promise.reject(
                                  new Error(
                                    "Please upload the drive credentials file!"
                                  )
                                );
                              else if (
                                instanceOfUploadedDriveCredentials(
                                  JSON.parse(driveCredentials)
                                )
                              )
                                return true;
                              else
                                return Promise.reject(
                                  new Error(
                                    "Drive credentials file is not valid!"
                                  )
                                );
                            },
                          },
                        ]
                      : []
                  }
                >
                  <Upload
                    className="upload_input"
                    accept=".json"
                    maxCount={1}
                    onRemove={() => {
                      setDriveCredentials(() => "");
                      fileForm.validateFields(["driveCredentials"]);
                    }}
                    beforeUpload={(file) => {
                      const reader = new FileReader();

                      reader.onload = (e) => {
                        setDriveCredentials(() => e.target?.result as string);
                        setTimeout(() => {
                          fileForm.validateFields(["driveCredentials"]);
                        }, 0);
                      };

                      reader.readAsText(file);

                      return false;
                    }}
                  >
                    <Button icon={<UploadIcon />}>Upload credentials</Button>
                  </Upload>
                </Form.Item>
              ) : (
                <>
                  {!pathValue &&
                    !fileListValue?.length &&
                    [
                      FileType.DRIVE,
                      FileType.EXCEL,
                      FileType.PDF,
                      FileType.PPTX,
                      FileType.WORD,
                    ].includes(dataSource.type as FileType) && (
                      <StyledOr>Or</StyledOr>
                    )}
                  <Form.Item
                    style={{
                      display:
                        !pathValue || isUploadedFile(pathValue)
                          ? "block"
                          : "none",
                    }}
                    name="file"
                    label={
                      <StyledLabel $required>Upload your file</StyledLabel>
                    }
                    rules={
                      !pathValue || isUploadedFile(pathValue)
                        ? [
                            {
                              validator: async () => {
                                if (
                                  isEmpty(
                                    fileForm.getFieldValue("file")?.fileList
                                  ) &&
                                  !isUploadedFile(pathValue)
                                ) {
                                  const message = "Please upload the file!";
                                  setUploadError(message);
                                  return Promise.reject(new Error(message));
                                }
                                if (uploadError)
                                  return Promise.reject(new Error(uploadError));
                                return true;
                              },
                            },
                          ]
                        : []
                    }
                  >
                    <Upload
                      {...uploadProps}
                      className="upload_input"
                      maxCount={1}
                      onRemove={() => {
                        fileForm.setFieldsValue({ path: "" });
                        setUploadError("");
                      }}
                      accept={FileFormat[
                        dataSource.type as FileFormatType
                      ].extensions.join(",")}
                      beforeUpload={beforeFileUpload}
                      customRequest={async ({ onSuccess, onError, file }) => {
                        upload(file as File, {
                          onSuccess: (message) => {
                            onSuccess?.(
                              `${BASE_URL}/storage/public/${message}`
                            );
                            setUploadError("");
                          },
                          onError: () => {
                            onError?.({} as any);
                            setUploadError(
                              "An error occurred while uploading the file"
                            );
                          },
                          onSettled: () => {
                            setTimeout(() => {
                              fileForm.validateFields(["file"]);
                            }, 0);
                          },
                        });
                      }}
                    >
                      <Button icon={<UploadIcon />}>Click to upload</Button>
                    </Upload>
                  </Form.Item>
                </>
              )}
            </Form>
          </StyledFileForm>
          <StyledFooterButtons isCreating={isCreating} isUpdating={isUpdating}>
            <Button
              className="submit-button"
              key="submit"
              type="primary"
              onClick={() => fileForm.submit()}
            >
              {formMode === "update"
                ? isUpdating
                  ? "Updating ..."
                  : "Update"
                : isCreating
                ? "Creating ..."
                : "Create"}
            </Button>
          </StyledFooterButtons>
        </>
      )}
    </>
  );
};

const StyledFileForm = styled.div<{ $maxHeight: string }>`
  .file-form {
    padding-left: 10%;
    padding-right: 10%;
  }
  .input {
    height: 40px;
    border-radius: 6px;
    background: #eff1fd !important;
    border: none;
    // background: #fff;
    box-shadow: 0px 4px 4px 0px #fff;
    font-size: 12px;
  }
  .ant-form-item-explain-error {
    font-size: 12px;
  }
  max-height: ${(p) => p.$maxHeight};
  overflow-y: auto;
  margin-block: 35px;
  // width: 40vw;
  min-width: 450px;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: #fff;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #8b8b8b;
    border-radius: 7px;
  }

  &::-webkit-scrollbar-button {
    display: block;
    height: 16px;
    width: 16px;
    background-color: #fff;
    border: none;
  }

  &::-webkit-scrollbar-button:single-button:vertical:decrement {
    height: 12px;
    width: 16px;
    background-position: center 6px;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='rgb(96, 96, 96)'><polygon points='50,00 0,50 100,50'/></svg>");
  }
  &::-webkit-scrollbar-button:single-button:vertical:increment {
    height: 12px;
    width: 16px;
    background-position: center center;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='rgb(96, 96, 96)'><polygon points='0,0 100,0 50,50'/></svg>");
  }
  .ant-btn-default:focus {
    box-shadow: none !important;
  }
  .ant-btn-default {
    border: none;
    box-shadow: none;
    background-color: #eff1fd;
    height: 40px;
    color: #000 !important;
    align-items: center;
    display: flex;
    border-radius: 6px;
    margin-bottom: 10px;
    font-size: 13px;
    img {
      height: 15px;
      width: 15px;
    }
  }
  .upload_input {
    background: #f6f8fc;
    position: relative;
  }
  .upload_input:after {
    position: absolute;
    top: 45px;
    left: 0;
    color: #6c757d;
  }
  .ant-upload-list {
    flex: 1;
    text-align: left;
    border: 1px solid #f6f8fc;
    background: #f6f8fc;

    .ant-upload-list-item-name {
      color: #000;
    }
  }
  .ant-upload-list-item {
    margin-top: 10px !important;
  }
  .ant-upload-list-item-container {
    background: #fff !important;
  }
  .types {
    margin-top: -20px;
  }
`;
const StyledFooterButtons = styled.div<{
  isCreating: boolean;
  isUpdating: boolean;
}>`
  .submit-button {
    position: absolute;
    right: 40px;
    bottom: 15px;
    font-size: 13px;
    width: 100px;
    cursor: ${({ isCreating, isUpdating }) =>
      isCreating || isUpdating ? "not-allowed" : "pointer"};
  }
`;
const StyledLabel = styled.span<{ $required?: boolean }>`
  color: rgba(4, 7, 38, 0.8);
  font-size: 13px;
  font-weight: 600;

  ${({ $required }) =>
    $required &&
    `padding-left: 10px;
    &::before {
      content: "*";
      color: #ff0000;
      font-family: SimSun, sans-serif;
      padding-top: 1px;
      position: absolute;
      left:0;
  }`}
`;
const StyledOr = styled.div`
  color: rgba(4, 7, 38, 0.8);
  font-size: 13px;
  font-weight: 600;
  text-align: center;
`;

export default UpdateFileForm;
